import { format } from 'date-fns';
import { Color, PerspectiveCamera } from 'three';

import { LinearUnit } from '../../../../../../utils/proj4/units';
import cameraManager from '../../../../../../viewer/camera-manager';
import {
  collectReportLayers,
  ReportRegion,
} from '../../../../../../viewer/report/collect-report-layers';
import { collectViewerState } from '../../../../../../viewer/report/collect-viewer-state';
import {
  OmittedLayer,
  RasterLayer,
  ReportAttribution,
  ReportManifest,
  ReportOutput,
  ReportState,
} from '../../../../../../viewer/report/manifest';
import { SkyView } from '../../../../../../viewer/skyview';
import {
  getReportFrameBounds,
  ReportFrameState,
} from '../../../../../../viewer/tools/utils/report-frame';
import { getMapAreaMm } from '../helpers/types';
import { backgroundMapHelpers, getBackgroundMapSize, getShownMapLayer } from './background-map';
import { TitleBlockValues } from './cad-drawing-title-block-fields';
import { PageSettings } from './page-settings';
import { gzipJson, ReportFiles, ReportPayload } from './report-zip';
import { screenshotHelpers } from './screenshot';

export type CadDrawingReportOptions = TitleBlockValues & {
  /** The frame around the map with the title block. */
  includeDrawingFrame: boolean;
  useWhiteBackground: boolean;
  useBlackLinesAndText: boolean;
  /** The width of thin lines in points, as the slider of the react-pdf report. */
  lineThickness: number;
  includeOrthophoto: boolean;
  /** The paper resolution of the orthophoto and the background map. */
  dpi: ReportDpi;
};

export const reportDpis = [150, 200, 300] as const;
export type ReportDpi = (typeof reportDpis)[number];

/** What every report is made with. */
export type ReportPayloadInput = {
  page: Omit<PageSettings, 'scale'>;
  options: CadDrawingReportOptions;
  projectId: string;
  projectName: string;
  /** The project's coordinate system as proj4, the unit and how many meters the unit is. */
  crs: { proj4: string; unit: LinearUnit; metersPerUnit: number };
  locale: string;
  /** The labels of the title block's fields, translated. */
  labels: Record<string, string>;
  date: Date;
};

export type CameraPayloadInput = ReportPayloadInput & {
  /** The file versions of legacy drawings that couldn't be converted, left out. */
  unconvertedVersionIds?: ReadonlySet<string>;
};

export type PlanPayloadInput = CameraPayloadInput & {
  page: PageSettings;
  frame: ReportFrameState;
};

const MM_PER_POINT = 25.4 / 72;

const MAP_LAYER_ID = 'map';
const MAP_FILE = 'rasters/map.png';
const SCREENSHOT_FILE = 'rasters/screenshot.png';

const logoExtensions: Record<string, string> = { 'image/png': 'png', 'image/jpeg': 'jpg' };

/** Decodes the logo's data URL to a file of the zip, if it's a PNG or a JPEG. */
function getLogoFile(dataUrl: string): { path: string; data: Uint8Array } | undefined {
  const match = /^data:([^;,]+);base64,(.*)$/.exec(dataUrl);
  const extension = match ? logoExtensions[match[1]] : undefined;
  if (!match || !extension) {
    return undefined;
  }

  const binary = atob(match[2]);
  const data = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return { path: `rasters/logo.${extension}`, data };
}

/**
 * Renders the background map shown in the viewer for the north-up box around the frame, at the
 * paper's resolution. Undefined when no map is shown; an omitted layer when it can't be rendered,
 * e.g. when its tiles don't load, so the rest of the report is still made.
 */
async function collectBackgroundMap(
  skyview: SkyView,
  input: PlanPayloadInput,
): Promise<
  | { layer: RasterLayer; png: Uint8Array; attributions: ReportAttribution[] }
  | OmittedLayer
  | undefined
> {
  const mapLayer = getShownMapLayer();
  if (!mapLayer || !skyview.isWmsMapAvailable()) {
    return undefined;
  }

  const box = getReportFrameBounds(input.frame);
  const size = getBackgroundMapSize(
    box,
    input.page.scale,
    input.crs.metersPerUnit,
    input.options.dpi,
  );

  try {
    const map = await backgroundMapHelpers.renderBackgroundMap(mapLayer, box, size);
    const attributions = map.attributions.map((text, index) => ({ id: `map-${index + 1}`, text }));
    return {
      layer: {
        id: MAP_LAYER_ID,
        kind: 'raster',
        file: MAP_FILE,
        extent: map.extent,
        opacity: 1,
        attributions: attributions.map((attribution) => attribution.id),
      },
      png: map.png,
      attributions,
    };
  } catch (error) {
    console.error('Failed to render the background map:', error);
    return { kind: 'backgroundMap', name: mapLayer };
  }
}

/**
 * Builds what every report has in common: the state's coordinate system, background, timeline,
 * camera and occlusion culling, and the output with the title block and its logo.
 */
function buildCommon(skyview: SkyView, input: ReportPayloadInput) {
  const { page, options } = input;
  const files: ReportFiles = {};

  const logo =
    options.includeDrawingFrame && options.uploadedImage
      ? getLogoFile(options.uploadedImage.dataUrl)
      : undefined;
  if (logo) {
    files[logo.path] = logo.data;
  }

  const state: ReportState = {
    crs: { proj4: input.crs.proj4, unit: input.crs.unit, toMetre: input.crs.metersPerUnit },
    ...collectViewerState(skyview),
    background:
      options.useWhiteBackground || !skyview.renderer
        ? '#ffffff'
        : `#${skyview.renderer.getClearColor(new Color()).getHexString()}`,
  };

  const output: Omit<ReportOutput, 'view'> = {
    format: 'pdf',
    report: 'cadDrawing',
    layout: page.layout,
    page: { size: page.size, orientation: page.orientation },
    rasterDpi: options.dpi,
    blackLinesAndText: options.useBlackLinesAndText,
    thinLineWidthMm: options.lineThickness * MM_PER_POINT,
    locale: input.locale,
    titleBlock: options.includeDrawingFrame
      ? {
          title: options.title,
          description: options.description,
          responsible: options.responsible,
          freeText: options.freeText,
          projectName: input.projectName,
          date: format(input.date, 'yyyy-MM-dd'),
          logo: logo?.path,
          labels: input.labels,
        }
      : undefined,
  };

  return { state, output, files };
}

/** Collects the layers in the region, with their vectors layers' files. */
function collectLayers(
  skyview: SkyView,
  input: ReportPayloadInput,
  region: ReportRegion,
  unconvertedVersionIds: ReadonlySet<string> | undefined,
  files: ReportFiles,
) {
  const { vectorFiles, ...layers } = collectReportLayers(skyview, {
    region,
    includeOrthophoto: input.options.includeOrthophoto,
    blackLinesAndText: input.options.useBlackLinesAndText,
    unconvertedVersionIds,
    dpi: input.options.dpi,
  });
  for (const [path, content] of Object.entries(vectorFiles)) {
    files[path] = gzipJson(content);
  }
  return layers;
}

/**
 * Builds the report zip's manifest and files for a drawing to scale of what is in the frame.
 */
export async function buildPlanPayload(
  skyview: SkyView,
  input: PlanPayloadInput,
): Promise<ReportPayload> {
  const { page, frame } = input;
  const { state, output, files } = buildCommon(skyview, input);
  const layers = collectLayers(
    skyview,
    input,
    { mode: 'plan', frame },
    input.unconvertedVersionIds,
    files,
  );

  // The background map is under everything, see renderOrders.
  const map = await collectBackgroundMap(skyview, input);
  if (map && 'layer' in map) {
    files[MAP_FILE] = map.png;
    layers.layers.unshift(map.layer);
    layers.paintOrder.unshift({ layer: map.layer.id });
  } else if (map) {
    layers.omitted.push(map);
  }

  const manifest: ReportManifest = {
    schemaVersion: 2,
    projectId: input.projectId,
    state: {
      ...state,
      ...layers,
      attributions: map && 'layer' in map ? map.attributions : [],
    },
    output: {
      ...output,
      view: {
        mode: 'plan',
        center: [frame.center.x, frame.center.y],
        width: frame.width,
        height: frame.height,
        rotation: frame.rotation,
        scale: page.scale,
      },
    },
  };

  return { manifest, files };
}

/**
 * Builds the report zip's manifest and files for the 3D view through the viewer's perspective
 * camera, which the backend renders: what the camera sees. A background map isn't sent, as the 3D
 * view doesn't show one.
 */
export function buildCameraPayload(
  skyview: SkyView,
  input: CameraPayloadInput,
): Promise<ReportPayload> {
  const camera = cameraManager().getActiveCamera()?.getTHREECamera();
  if (!(camera instanceof PerspectiveCamera)) {
    throw new Error('The camera view needs a perspective camera.');
  }

  const { state, output, files } = buildCommon(skyview, input);
  const layers = collectLayers(
    skyview,
    input,
    { mode: 'camera', camera },
    input.unconvertedVersionIds,
    files,
  );

  const manifest: ReportManifest = {
    schemaVersion: 2,
    projectId: input.projectId,
    state: { ...state, ...layers, attributions: [] },
    output: { ...output, view: { mode: 'camera' } },
  };

  return Promise.resolve({ manifest, files });
}

/**
 * Builds the report zip's manifest and files for a picture of the view, cropped to the page's map
 * area: for what the backend can't render, a panorama's 360° images. The state has what the camera
 * sees too, as every view's, so that it can be restored.
 */
export async function buildScreenshotPayload(
  skyview: SkyView,
  input: ReportPayloadInput,
): Promise<ReportPayload> {
  const { state, output, files } = buildCommon(skyview, input);
  const mapArea = getMapAreaMm(input.page.layout, input.page.size, input.page.orientation);

  const camera = cameraManager().getActiveCamera()?.getTHREECamera();
  const layers =
    camera instanceof PerspectiveCamera
      ? collectLayers(skyview, input, { mode: 'camera', camera }, undefined, files)
      : undefined;

  files[SCREENSHOT_FILE] = await screenshotHelpers.captureScreenshot(
    skyview,
    mapArea.width / mapArea.height,
    input.options.useWhiteBackground,
  );

  const manifest: ReportManifest = {
    schemaVersion: 2,
    projectId: input.projectId,
    state: {
      ...state,
      ...layers,
      ...(layers ? { attributions: [] } : {}),
      screenshot: SCREENSHOT_FILE,
    },
    output: { ...output, view: { mode: 'screenshot' } },
  };

  return { manifest, files };
}
